import { KeyCode } from '../enums/index';
import { SlickCellRangeSelector } from './index';
export class SlickCellSelectionModel {
    constructor(options) {
        this._canvas = null;
        this._ranges = [];
        this._defaults = {
            selectActiveCell: true,
        };
        this.onSelectedRangesChanged = new Slick.Event();
        this.pluginName = 'CellSelectionModel';
        this._eventHandler = new Slick.EventHandler();
        if (options === undefined || options.cellRangeSelector === undefined) {
            this._selector = new SlickCellRangeSelector({ selectionCss: { border: '2px solid black' } });
        }
        else {
            this._selector = options.cellRangeSelector;
        }
        this._addonOptions = options;
    }
    get addonOptions() {
        return this._addonOptions;
    }
    get canvas() {
        return this._canvas;
    }
    get cellRangeSelector() {
        return this._selector;
    }
    get eventHandler() {
        return this._eventHandler;
    }
    init(grid) {
        this._grid = grid;
        this._addonOptions = { ...this._defaults, ...this._addonOptions };
        this._eventHandler
            .subscribe(this._grid.onActiveCellChanged, this.handleActiveCellChange.bind(this))
            .subscribe(this._grid.onKeyDown, this.handleKeyDown.bind(this))
            .subscribe(this._selector.onBeforeCellRangeSelected, this.handleBeforeCellRangeSelected.bind(this))
            .subscribe(this._selector.onCellRangeSelected, this.handleCellRangeSelected.bind(this));
        // register the cell range selector plugin
        grid.registerPlugin(this._selector);
        this._canvas = this._grid.getCanvasNode();
    }
    destroy() {
        this.dispose();
    }
    dispose() {
        var _a, _b;
        this._canvas = null;
        if (this._selector) {
            this._selector.onBeforeCellRangeSelected.unsubscribe(this.handleBeforeCellRangeSelected.bind(this));
            this._selector.onCellRangeSelected.unsubscribe(this.handleCellRangeSelected.bind(this));
        }
        this._eventHandler.unsubscribeAll();
        (_a = this._grid) === null || _a === void 0 ? void 0 : _a.unregisterPlugin(this._selector);
        (_b = this._selector) === null || _b === void 0 ? void 0 : _b.dispose();
    }
    getSelectedRanges() {
        return this._ranges;
    }
    rangesAreEqual(range1, range2) {
        let areDifferent = (range1.length !== range2.length);
        if (!areDifferent) {
            for (let i = 0; i < range1.length; i++) {
                if (range1[i].fromCell !== range2[i].fromCell
                    || range1[i].fromRow !== range2[i].fromRow
                    || range1[i].toCell !== range2[i].toCell
                    || range1[i].toRow !== range2[i].toRow) {
                    areDifferent = true;
                    break;
                }
            }
        }
        return !areDifferent;
    }
    refreshSelections() {
        this.setSelectedRanges(this.getSelectedRanges());
    }
    removeInvalidRanges(ranges) {
        const result = [];
        for (let i = 0; i < ranges.length; i++) {
            const r = ranges[i];
            if (this._grid.canCellBeSelected(r.fromRow, r.fromCell) && this._grid.canCellBeSelected(r.toRow, r.toCell)) {
                result.push(r);
            }
        }
        return result;
    }
    setSelectedRanges(ranges, caller = 'SlickCellSelectionModel.setSelectedRanges') {
        // simple check for: empty selection didn't change, prevent firing onSelectedRangesChanged
        if ((!this._ranges || this._ranges.length === 0) && (!ranges || ranges.length === 0)) {
            return;
        }
        // if range has not changed, don't fire onSelectedRangesChanged
        const rangeHasChanged = !this.rangesAreEqual(this._ranges, ranges);
        this._ranges = this.removeInvalidRanges(ranges);
        if (rangeHasChanged) {
            const eventData = new Slick.EventData();
            Object.defineProperty(eventData, 'detail', { writable: true, configurable: true, value: { caller } });
            this.onSelectedRangesChanged.notify(this._ranges, eventData);
        }
    }
    //
    // protected functions
    // ---------------------
    handleActiveCellChange(_e, args) {
        var _a, _b;
        if (((_a = this._addonOptions) === null || _a === void 0 ? void 0 : _a.selectActiveCell) && args.row !== null && args.cell !== null) {
            this.setSelectedRanges([new Slick.Range(args.row, args.cell)]);
        }
        else if (!((_b = this._addonOptions) === null || _b === void 0 ? void 0 : _b.selectActiveCell)) {
            // clear the previous selection once the cell changes
            this.setSelectedRanges([]);
        }
    }
    handleBeforeCellRangeSelected(e) {
        if (this._grid.getEditorLock().isActive()) {
            e.stopPropagation();
            return false;
        }
    }
    handleCellRangeSelected(_e, args) {
        this._grid.setActiveCell(args.range.fromRow, args.range.fromCell, false, false, true);
        this.setSelectedRanges([args.range]);
    }
    handleKeyDown(e) {
        let ranges;
        let last;
        const active = this._grid.getActiveCell();
        const metaKey = e.ctrlKey || e.metaKey;
        if (active && e.shiftKey && !metaKey && !e.altKey &&
            (e.which === KeyCode.LEFT || e.key === 'ArrowLeft'
                || e.which === KeyCode.RIGHT || e.key === 'ArrowRight'
                || e.which === KeyCode.UP || e.key === 'ArrowUp'
                || e.which === KeyCode.DOWN || e.key === 'ArrowDown')) {
            ranges = this.getSelectedRanges().slice();
            if (!ranges.length) {
                ranges.push(new Slick.Range(active.row, active.cell));
            }
            // keyboard can work with last range only
            last = ranges.pop();
            if (typeof (last === null || last === void 0 ? void 0 : last.contains) === 'function') {
                // can't handle selection out of active cell
                if (!last.contains(active.row, active.cell)) {
                    last = new Slick.Range(active.row, active.cell);
                }
                let dRow = last.toRow - last.fromRow;
                let dCell = last.toCell - last.fromCell;
                // walking direction
                const dirRow = active.row === last.fromRow ? 1 : -1;
                const dirCell = active.cell === last.fromCell ? 1 : -1;
                if (e.which === KeyCode.LEFT || e.key === 'ArrowLeft') {
                    dCell -= dirCell;
                }
                else if (e.which === KeyCode.RIGHT || e.key === 'ArrowRight') {
                    dCell += dirCell;
                }
                else if (e.which === KeyCode.UP || e.key === 'ArrowUp') {
                    dRow -= dirRow;
                }
                else if (e.which === KeyCode.DOWN || e.key === 'ArrowDown') {
                    dRow += dirRow;
                }
                // define new selection range
                const newLast = new Slick.Range(active.row, active.cell, active.row + dirRow * dRow, active.cell + dirCell * dCell);
                if (this.removeInvalidRanges([newLast]).length) {
                    ranges.push(newLast);
                    const viewRow = dirRow > 0 ? newLast.toRow : newLast.fromRow;
                    const viewCell = dirCell > 0 ? newLast.toCell : newLast.fromCell;
                    this._grid.scrollRowIntoView(viewRow);
                    this._grid.scrollCellIntoView(viewRow, viewCell, false);
                }
                else {
                    ranges.push(last);
                }
                this.setSelectedRanges(ranges);
                e.preventDefault();
                e.stopPropagation();
            }
        }
    }
}
